import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import * as XLSX from 'xlsx';
import { Html5Qrcode } from 'html5-qrcode';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { InviteSheet } from '../pages/InviteSheetComponent';
import './event.css';
import ExcelJS from 'exceljs';

const API_URL = import.meta.env.VITE_API_URL;
const 曜日 = ['日', '月', '火', '水', '木', '金', '土'];
const SHOW_ID_COL = false;

// 日付を「2025/01/15(水)」形式に
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}(${曜日[date.getDay()]})`;
}

function formatmdDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${m}/${d}(${曜日[date.getDay()]})`;
}

// 時刻を「14：30」形式に
function formatTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}：${m}`;
}

// イベントから開始・終了日時を取得
function parseEventDates(event) {
  const parseDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const time = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const d = new Date(`${dateStr}T${time}`);
    return isNaN(d) ? null : d;
  };

  const parseISO = (iso) => {
    if (!iso || typeof iso !== 'string' || !iso.includes('T')) return null;
    const d = new Date(iso);
    return isNaN(d) ? null : d;
  };

  const start = parseDateTime(event.start_date, event.start_time)
             || parseISO(event.start_time_iso || event.start_time);
  const end = parseDateTime(event.end_date, event.end_time)
           || parseISO(event.end_time_iso || event.end_time);

  return { start, end };
}

// 期間を表示用文字列に
function formatPeriod(event) {
  const { start_date, end_date, start_time, end_time, times_unified, schedules } = event;

  // 複数日程で時刻が異なる場合は日程ごとに表示
  if (schedules && schedules.length > 1 && !times_unified) {
    return schedules.map(s => {
      const d = new Date(s.date);
      const st = s.start_time ? s.start_time.slice(0, 5) : '';
      const et = s.end_time   ? s.end_time.slice(0, 5)   : '';
      const receptPart = (s.recept_start_time && s.recept_end_time)
        ? `　受付（${s.recept_start_time.slice(0, 5)}～${s.recept_end_time.slice(0, 5)}）`
        : '';
      return `${formatDate(d)}　${st}～${et}${receptPart}`;
    }).join(' / ');
  }

  const isSingleDay = start_date === end_date;
  const { start, end } = parseEventDates(event);

  let baseText;
  if (!start && !end) {
    baseText = '—';
  } else if (isSingleDay) {
    baseText = start && end
      ? `${formatDate(start)}　${formatTime(start)}～${formatTime(end)}`
      : `${formatDate(start || end)}　${formatTime(start || end)}`;
  } else {
    baseText = `${formatDate(new Date(start_date))}～${formatDate(new Date(end_date))}　${formatTime(start)}～${formatTime(end)}`;
  }

  // 受付時間（schedulesがある場合は先頭日程から取得）
  const receptStart = schedules?.[0]?.recept_start_time || event.recept_start;
  const receptEnd   = schedules?.[0]?.recept_end_time   || event.recept_end;
  if (receptStart && receptEnd) {
    const fmt = (t) => String(t).slice(0, 5).replace(':', '：');
    baseText += `　受付（${fmt(receptStart)}～${fmt(receptEnd)}）`;
  }

  return baseText;
}

function generateDatetimeSlots(event, mode) {
  const schedules = event.schedules || [];
  if (schedules.length === 0) return [];

  const intervalMin = mode === '60min' ? 60 : 30;

  const parseHHMM = (timeStr) => {
    if (!timeStr) return null;
    const parts = String(timeStr).split(':');
    return { h: parseInt(parts[0], 10), m: parseInt(parts[1], 10) };
  };

  const slots = [];

  for (const sc of schedules) {
    const day = new Date(sc.date);
    const receptStart = parseHHMM(sc.recept_start_time);
    const receptEnd   = parseHHMM(sc.recept_end_time);
    const evStart     = parseHHMM(sc.start_time);
    const evEnd       = parseHHMM(sc.end_time);

    const fromH = receptStart ? receptStart.h : (evStart ? evStart.h : 9);
    const fromM = receptStart ? receptStart.m : (evStart ? evStart.m : 0);
    const toH   = receptEnd   ? receptEnd.h   : (evEnd   ? evEnd.h   : 17);
    const toM   = receptEnd   ? receptEnd.m   : (evEnd   ? evEnd.m   : 0);

    const slotCur = new Date(day);
    slotCur.setHours(fromH, fromM, 0, 0);

    if (!receptStart) {
      const rem = slotCur.getMinutes() % intervalMin;
      if (rem !== 0) {
        slotCur.setMinutes(slotCur.getMinutes() - rem + intervalMin);
      }
    }

    const slotEnd = new Date(day);
    slotEnd.setHours(toH, toM, 0, 0);

    while (slotCur <= slotEnd) {
      slots.push(new Date(slotCur));
      slotCur.setMinutes(slotCur.getMinutes() + intervalMin);
    }
  }

  return slots;
}


// テキスト正規化（検索用）
function normalizeText(value) {
  return String(value ?? '').normalize('NFKC').toLowerCase();
}

// 参加者のグループ回答を取得
function getGroupText(participant, groupId) {
  const selections = participant.selections || [];
  const names = selections
    .filter(s => s.event_group_id === groupId && s.event_type_name)
    .map(s => s.event_type_name);
  return names.join('、');
}

// ファイル名に使用できない文字を置換
function sanitizeFilename(name) {
  return String(name || '').replace(/[<>:"/\\|?*]/g, '_').trim() || 'unnamed';
}

function toLocalDatetimeString(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

// DatetimeComboBox: 入力しても選択肢が全件表示されるコンボボックス
function DatetimeComboBox({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapRef} className="hev-comboBox">
      <input
        className="hev-input datetimeSelect"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || '選択または入力してください'}
        autoComplete="off"
      />
      {open && options.length > 0 && (
        <ul className="hev-comboBox__dropdown">
          {options.map((val, i) => (
            <li
              key={i}
              className={`hev-comboBox__item${val === value ? ' hev-comboBox__item--selected' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(val);
                setOpen(false);
              }}
            >
              {val}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// 参加者行コンポーネント
function ParticipantRow({
  participant,
  groupColumns,
  isTodayInRange,
  checkinType,
  showAttendanceCol,
  onInvite,
  onQr,
  onCheckIn,
  onCheckOut,
  onEdit,
  onDelete,
}) {
  const p = participant;
  const inStr = p.check_in_time ? new Date(p.check_in_time).toLocaleString() : '';
  const outStr = p.check_out_time ? new Date(p.check_out_time).toLocaleString() : '';

  const renderCheckButton = (label, time, onClick) => (
    <div className="hev-cellFlex">
      {time && <span className="hev-timeText">{time}</span>}
      <button
        className="hev-btn hev-btn--ghost hev-pushRight"
        onClick={onClick}
        title="登録"
      >
        ✓
      </button>
    </div>
  );

  const renderActionButton = () => {
    const type = (checkinType ?? '').trim();
    if (!type) return '-';

    const isWebDisplay = type.toUpperCase() === 'WEB表示';
    return (
      <button className="hev-btn" onClick={() => isWebDisplay ? onQr(p) : onInvite(p)}>
        {isWebDisplay ? 'QR表示' : '招待状'}
      </button>
    );
  };

  return (
    <tr>
      {SHOW_ID_COL && <td>{p.participant_id}</td>}
      <td>{p.part_type_name}</td>

      {showAttendanceCol && (
        <td>
          <span
            className={`hev-attendance hev-attendance--${
              p.attendance_status === '参加'
                ? 'yes'
                : p.attendance_status === '不参加'
                ? 'no'
                : p.attendance_status === 'キャンセル'
                ? 'cancel'
                : 'pending'
            }`}
          >
            {p.attendance_status || '未回答'}
          </span>
        </td>
      )}

      <td>{p.participant_name}</td>
      <td>{p.participant_email}</td>
      <td>{p.participant_phone || ''}</td>
      <td>{p.participant_note || ''}</td>

      {groupColumns.map(g => (
        <td key={g.event_group_id}>{getGroupText(p, g.event_group_id)}</td>
      ))}

      {p.participant_id ? (
        <>
          <td>{renderCheckButton('チェックイン', inStr, () => onCheckIn(p))}</td>
          <td>{renderCheckButton('チェックアウト', outStr, () => onCheckOut(p))}</td>
        </>
      ) : (
        <>
          <td><span style={{ color: '#9ca3af' }}>未登録</span></td>
          <td><span style={{ color: '#9ca3af' }}>未登録</span></td>
        </>
      )}

      <td className="hev-ops">
        {p.participant_id ? (
          <>
            {renderActionButton()}
            <button className="hev-btn hev-btn--ghost" onClick={() => onEdit(p)}>編集</button>
            <button className="hev-btn hev-btn--danger" onClick={() => onDelete(p)}>削除</button>
          </>
        ) : (
          <span style={{ color: '#9ca3af' }}>未登録</span>
        )}
      </td>
    </tr>
  );
}

// 日付だけ（YYYYMMDD）を比較用に取り出す
function toYmdNumber(date) {
  return date.getFullYear() * 10000 +
         (date.getMonth() + 1) * 100 +
         date.getDate();
}

// ファイル名フィールドの定義
const FILENAME_FIELDS = [
  { id: 'participant_name', label: '参加者名', getValue: (p) => p.participant_name || '' },
  { id: 'participant_email', label: 'メールアドレス', getValue: (p) => p.participant_email || '' },
  { id: 'participant_phone', label: '電話番号', getValue: (p) => p.participant_phone || '' },
  { id: 'participant_note', label: '備考', getValue: (p) => p.participant_note || '' },
  { id: 'qr_code', label: 'QRコード', getValue: (p) => p.qr_code || '' },
  { id: 'participant_id', label: '参加者ID', getValue: (p) => String(p.participant_id || '') },
  { id: 'part_type_name', label: '種別', getValue: (p) => p.part_type_name || '' },
];

const SEPARATORS = [
  { id: '_', label: 'アンダースコア (_)' },
  { id: '-', label: 'ハイフン (-)' },
  { id: '.', label: 'ドット (.)' },
  { id: ' ', label: 'スペース' },
];

// メインコンポーネント
export default function HostEventDetail() {
  const { invite_token } = useParams();

  // State
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState(''); 

  // 参加者追加モーダル
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMail, setNewMail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newNote, setNewNote] = useState('');
  const [groupSelections, setGroupSelections] = useState({});
  const [groupTexts, setGroupTexts] = useState({});

  // 参加者編集モーダル
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [editMail, setEditMail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editGroupSelections, setEditGroupSelections] = useState({});
  const [editGroupTexts, setEditGroupTexts] = useState({});

  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editStatus, setEditStatus] = useState('');

  // 受付モーダル
  const [receptionOpen, setReceptionOpen] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [cameraAvailable, setCameraAvailable] = useState(true);    // ← 追加
  const [cameraError, setCameraError] = useState('');              // ← 追加

  // 一括PDF保存モーダル
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
  const [selectedFields, setSelectedFields] = useState(['participant_name']);
  const [separator, setSeparator] = useState('_');
  const [filenamePrefix, setFilenamePrefix] = useState('');
  const [filenameSuffix, setFilenameSuffix] = useState('');
  const [currentRenderingParticipant, setCurrentRenderingParticipant] = useState(null);
  const [inviteDataCache, setInviteDataCache] = useState({});

  // ユーザー招待モーダル
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteCandidates, setInviteCandidates] = useState([]);
  const [inviteSelectedIds, setInviteSelectedIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  // 項目別カウント
  const [metricsData, setMetricsData] = useState({ groups: [], participants: [] });
  const [selectionFilter, setSelectionFilter] = useState([]);
  const [selectionDropdownOpen, setSelectionDropdownOpen] = useState(false);
  const selectionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (selectionDropdownRef.current && !selectionDropdownRef.current.contains(e.target)) {
        setSelectionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredInviteCandidates = useMemo(() => {
    const needle = normalizeText(inviteSearch);
    if (!needle) return inviteCandidates;
    return inviteCandidates.filter(u => {
      const text = normalizeText([u.user_name, u.user_email, u.phone].join(' '));
      return text.includes(needle);
    });
  }, [inviteCandidates, inviteSearch]);

  // Refs
  const qrScannerRef = useRef(null);
  const processingRef = useRef(false);
  const lastScanRef = useRef({ code: '', ts: 0 });
  const pdfSheetRef = useRef(null);
  const scanInputRef = useRef(null);

  // scanCode用のbufferをrefで管理（stateの非同期更新を避ける）
  const scanBufferRef = useRef('');

  useEffect(() => {
    if (!receptionOpen) return;
    setScanCode('');
    scanBufferRef.current = '';

    const handleKeyDown = (e) => {
      // IME処理中は無視
      if (e.isComposing || e.keyCode === 229) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const code = scanBufferRef.current.normalize('NFKC');
        if (code) {
          handleScanFound(code);
          scanBufferRef.current = '';
          setScanCode('');
        }
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        scanBufferRef.current = scanBufferRef.current.slice(0, -1);
        setScanCode(scanBufferRef.current);
        return;
      }

      if (e.key.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        scanBufferRef.current += e.key;
        setScanCode(scanBufferRef.current.normalize('NFKC'));
      }
    };

    // capture: true でIMEより前に介入
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [receptionOpen]);
  
  // グループ入力の初期化（追加用）
  const initGroupInputs = (ev) => {
    const selections = {};
    const texts = {};
    (ev?.event_groups || []).forEach(g => {
      const types = g.types || [];
      const isDatetime = (g.description || '').startsWith('日時');
      const isFreeText = !isDatetime && types.length === 1 && types[0].event_type_name == '';
      if (isDatetime || isFreeText) {
        texts[g.event_group_id] = '';
      } else {
        selections[g.event_group_id] = '';
      }
    });
    setGroupSelections(selections);
    setGroupTexts(texts);
  };

  // 参加者のグループ回答を編集フォームにセット（編集用）
  const initEditGroupInputsFromParticipant = (ev, participant) => {
    const baseSelections = {};
    const baseTexts = {};

    const groups = ev?.event_groups || [];
    const selections = participant.selections || [];

    groups.forEach((g) => {
      const gid = g.event_group_id;
      const types = g.types || [];
      const isFreeText =
        types.length === 1 && types[0].event_type_name === '';

      const answersForGroup = selections.filter(
        (s) => s.event_group_id === gid
      );

      if (answersForGroup.length === 0) {
        if (isFreeText) {
          baseTexts[gid] = '';
        } else {
          baseSelections[gid] = '';
        }
        return;
      }

      const firstAnswer = answersForGroup[0];
      const selectedName = firstAnswer.event_type_name || '';

      if (isFreeText) {
        baseTexts[gid] = selectedName;
      } else {
        const matchedType = types.find(
          (t) => (t.event_type_name || '') === selectedName
        );
        baseSelections[gid] = matchedType
          ? String(matchedType.event_type_id)
          : '';
      }
    });

    setEditGroupSelections(baseSelections);
    setEditGroupTexts(baseTexts);
  };

  // 編集開始
  const openEditModal = (p) => {
    if (!event) return;
    setEditingParticipant(p);
    setEditName(p.participant_name || '');
    setEditMail(p.participant_email || '');
    setEditPhone(p.participant_phone || '');
    setEditNote(p.participant_note || '');
    setEditCheckIn(p.check_in_time ? toLocalDatetimeString(p.check_in_time) : '');
    setEditCheckOut(p.check_out_time ? toLocalDatetimeString(p.check_out_time) : '');
    setEditStatus(p.attendance_status || ''); 
    initEditGroupInputsFromParticipant(event, p);
    setEditModalOpen(true);
  };
  
  // 招待候補ユーザー一覧を取得してモーダルを開く
  const openInviteModal = async () => {
    setInviteModalOpen(true);
    setInviteLoading(true);
    setInviteSelectedIds([]);
    setInviteSearch('');
    try {
      const res = await axios.get(
        `${API_URL}/api/operator/events/${invite_token}/invite-candidates/`);
      if (res.data?.success) {
        setInviteCandidates(res.data.users || []);
      } else {
        alert('候補ユーザーの取得に失敗しました');
        setInviteCandidates([]);
      }
    } catch (e) {
      console.error(e);
      alert('候補ユーザーの取得に失敗しました');
    } finally {
      setInviteLoading(false);
    }
  };

  const toggleInviteUser = (uid) => {
    setInviteSelectedIds(prev =>
      prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
    );
  };

  const submitInvite = async () => {
    if (inviteSelectedIds.length === 0) return;
    setInviting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/operator/events/${invite_token}/invite-users/`,
        { user_ids: inviteSelectedIds }
      );
      if (res.data?.success) {
        alert(`${res.data.created_count}名を招待しました`);
        setInviteModalOpen(false);
        await fetchDetail();
      } else {
        alert('招待に失敗しました');
      }
    } catch (e) {
      console.error(e);
      alert('招待に失敗しました');
    } finally {
      setInviting(false);
    }
  };

  // イベント詳細を取得
  const fetchDetail = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/operator/events/${invite_token}/`);
      if (res.data?.success) {
        const ev = res.data.event || null;
        setEvent(ev);
        setParticipants(res.data.participants || []);
        setInviteUrl(res.data.invite_url || '');
        setMetricsData({
          groups:       res.data.selection_groups || [],
          participants: res.data.selection_participants || [],
        });
        initGroupInputs(ev);
      } else {
        setError('詳細の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setError('詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 参加者を追加
  const addParticipant = async () => {
    if (!canSubmitAdd) return;
    setSaving(true);
    try {
      const answers = [
        ...Object.entries(groupSelections).map(([gid, typeId]) => ({
          event_group_id: Number(gid),
          event_type_id: typeId ? Number(typeId) : null,
          free_text: null,
        })),
        ...Object.entries(groupTexts).map(([gid, text]) => ({
          event_group_id: Number(gid),
          event_type_id: null,
          free_text: text.trim() || null,
        })),
      ];
      
      await axios.post(
        `${API_URL}/api/operator/events/${invite_token}/participants/`,
        {
          participant_name: newName.trim(),
          participant_email: newMail.trim(),
          participant_phone: newPhone.trim() || null,
          participant_note: newNote.trim() || null,
          group_answers: answers,
        }
      );

      setNewName('');
      setNewMail('');
      setNewPhone('');
      setNewNote('');
      initGroupInputs(event);
      await fetchDetail();
      setAddModalOpen(false);
    } catch (e) {
      console.error(e);
      alert('追加に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 参加者を修正
  const updateParticipant = async () => {
    if (!editingParticipant) return;
    if (!canSubmitEdit) return;

    setEditSaving(true);
    try {
      const answers = [
        ...Object.entries(editGroupSelections).map(([gid, typeId]) => ({
          event_group_id: Number(gid),
          event_type_id: typeId ? Number(typeId) : null,
          free_text: null,
        })),
        ...Object.entries(editGroupTexts).map(([gid, text]) => ({
          event_group_id: Number(gid),
          event_type_id: null,
          free_text: text.trim() || null,
        })),
      ];

     
      await axios.put(
        `${API_URL}/api/operator/events/${invite_token}/participants/${editingParticipant.participant_id}/`,
        {
          participant_name: editName.trim(),
          participant_email: editMail.trim(),
          participant_phone: editPhone.trim() || null,
          participant_note: editNote.trim() || null,
          check_in_time: editCheckIn || null,
          check_out_time: editCheckOut || null,
          participant_status: showAttendanceCol ? (editStatus || null) : null,
          group_answers: answers,
        }
      );

      setEditModalOpen(false);
      setEditingParticipant(null);
      await fetchDetail();
    } catch (e) {
      console.error(e);
      alert('参加者の更新に失敗しました');
    } finally {
      setEditSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`${p.participant_name} さんを削除しますか？`)) return;

    
    await axios.delete(
      `${API_URL}/api/operator/events/${invite_token}/participants/${p.participant_id}/delete/`);
    await fetchDetail();
  };

  // チェックイン/アウト登録
  const postCheck = async (p, kind) => {
    try {
      const existing = kind === 'checkin' ? p.check_in_time : p.check_out_time;
      const label = kind === 'checkin' ? 'チェックイン' : 'チェックアウト';

      if (existing && !window.confirm(`すでに${label}済みです。上書きしますか？`)) {
        return false;
      }

     
      await axios.post(
        `${API_URL}/api/operator/events/${invite_token}/participants/${p.participant_id}/${kind}/`);
      await fetchDetail();
      return true;
    } catch (e) {
      console.error(e);
      alert(`${kind === 'checkin' ? 'チェックイン' : 'チェックアウト'}の登録に失敗しました`);
    }
  };

  // 招待状/QR表示
  const showInvite = (p) => {
    window.open(`/inviteprint?inviter=${encodeURIComponent(p.invite_token)}&code=${encodeURIComponent(p.qr_code)}`, '_blank');
  };
  const showQr = (p) => {
    window.open(`/qrdisplay?inviter=${encodeURIComponent(p.invite_token)}&code=${encodeURIComponent(p.qr_code)}`, '_blank');
  };

  // 招待状データを取得（PDF生成用）
  const fetchInviteData = async (p) => {
    const cacheKey = `${p.invite_token}_${p.qr_code}`;
    if (inviteDataCache[cacheKey]) {
      return inviteDataCache[cacheKey];
    }

    try {
      const res = await axios.get(
        `${API_URL}/api/invite-view/${p.invite_token}/${p.qr_code}/invite/`);
      if (res.data?.success) {
        setInviteDataCache(prev => ({ ...prev, [cacheKey]: res.data }));
        return res.data;
      }
    } catch (e) {
      console.error('招待状データ取得エラー:', e);
    }
    return null;
  };

  // ファイル名を生成
  const generateFilename = useCallback((participant) => {
  const parts = [];
  
  if (filenamePrefix.trim()) {
    parts.push(sanitizeFilename(filenamePrefix.trim()));
  }
  
  selectedFields.forEach(fieldId => {
    if (fieldId === 'participant_name') {
      const value = participant.participant_name;
      if (value) {
        parts.push(sanitizeFilename(value));
      }
    } else if (fieldId.startsWith('group_')) {
      const groupId = Number(fieldId.replace('group_', ''));
      const value = getGroupText(participant, groupId);
      if (value) {
        parts.push(sanitizeFilename(value));
      }
    }
  });
  
  if (filenameSuffix.trim()) {
    parts.push(sanitizeFilename(filenameSuffix.trim()));
  }
  
  if (parts.length === 0) {
    parts.push(sanitizeFilename(participant.participant_name || 'unnamed'));
  }
  
  return parts.join(separator) + '.pdf';
}, [selectedFields, separator, filenamePrefix, filenameSuffix]);

  // 一括PDF生成
  const generatePdfs = async () => {
    const targetParticipants = filteredParticipants.length > 0 ? filteredParticipants : participants;
    
    if (targetParticipants.length === 0) {
      alert('対象となる参加者がいません');
      return;
    }

    // 招待状が表示できるかチェック
    const checkinType = (event?.checkin_type ?? '').trim();
    if (!checkinType) {
      alert('このイベントでは招待状を表示できません');
      return;
    }

    setPdfGenerating(true);
    setPdfProgress({ current: 0, total: targetParticipants.length });

    const zip = new JSZip();
    const errors = [];

    // ここを追加：同名ファイル用カウンタ
    const filenameCountMap = {};

    // ここを追加：同名のときだけ連番を付与する関数
    const makeUniqueFilename = (filename) => {
      // 拡張子(pdf)を分離して、ベース名だけでカウント
      const lastDot = filename.lastIndexOf('.');
      let base = filename;
      let ext = '';

      if (lastDot > 0) {
        base = filename.slice(0, lastDot);
        ext = filename.slice(lastDot); // 例: ".pdf"
      }

      const current = filenameCountMap[base] || 0;
      const next = current + 1;
      filenameCountMap[base] = next;

      if (next === 1) {
        // 1つ目はそのまま
        return filename;
      }

      const padded = String(next).padStart(2, '0'); // 02, 03...
      return `${base}_${padded}${ext}`;
    };

    try {
      for (let i = 0; i < targetParticipants.length; i++) {
        const p = targetParticipants[i];
        setPdfProgress({ current: i + 1, total: targetParticipants.length });

        try {
          // 招待状データを取得
          const inviteData = await fetchInviteData(p);
          if (!inviteData) {
            errors.push(`${p.participant_name}: 招待状データの取得に失敗`);
            continue;
          }

          // 参加者をレンダリング用にセット
          setCurrentRenderingParticipant({
            event: inviteData.event,
            participant: inviteData.participant,
            qrCode: p.qr_code
          });

          // レンダリング完了を待つ
          await new Promise(resolve => setTimeout(resolve, 500));

          // html2canvasでキャプチャ
          const sheetElement = pdfSheetRef.current;
          if (!sheetElement) {
            errors.push(`${p.participant_name}: シート要素が見つかりません`);
            continue;
          }

          const canvas = await html2canvas(sheetElement, {
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
          });

          // jsPDFでPDF作成（A4サイズ）
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
          });

          const imgData = canvas.toDataURL('image/png');
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          
          const imgAspect = canvas.width / canvas.height;
          const pdfAspect = pdfWidth / pdfHeight;

          let drawWidth, drawHeight, drawX, drawY;

          if (imgAspect > pdfAspect) {
            // 画像が横長 → 幅を基準にスケール
            drawWidth = pdfWidth;
            drawHeight = pdfWidth / imgAspect;
            drawX = 0;
            drawY = (pdfHeight - drawHeight) / 2;
          } else {
            // 画像が縦長または同じ → 高さを基準にスケール
            drawHeight = pdfHeight;
            drawWidth = pdfHeight * imgAspect;
            drawX = (pdfWidth - drawWidth) / 2;
            drawY = 0;
          }

          pdf.addImage(imgData, 'PNG', drawX, drawY, drawWidth, drawHeight);

          // ZIPに追加
          const rawFilename = generateFilename(p);   // 既存の関数そのまま利用
          const filename = makeUniqueFilename(rawFilename); // ここで重複を解決
          const pdfBlob = pdf.output('blob');
          zip.file(filename, pdfBlob);

        } catch (err) {
          console.error(`PDF生成エラー (${p.participant_name}):`, err);
          errors.push(`${p.participant_name}: ${err.message}`);
        }
      }

      // ZIPファイルをダウンロード
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFilename = `招待状_${event?.event_title || 'event'}_${new Date().toISOString().slice(0, 10)}.zip`;
      saveAs(zipBlob, zipFilename);

      if (errors.length > 0) {
        alert(`一部の招待状でエラーが発生しました:\n${errors.join('\n')}`);
      } else {
        alert(`${targetParticipants.length}件の招待状PDFを生成しました`);
      }

    } catch (err) {
      console.error('一括PDF生成エラー:', err);
      alert('PDF生成中にエラーが発生しました');
    } finally {
      setPdfGenerating(false);
      setCurrentRenderingParticipant(null);
      setPdfModalOpen(false);
    }
};


  // フィールド選択のトグル
  const toggleField = (fieldId) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter(id => id !== fieldId);
      }
      return [...prev, fieldId];
    });
  };

  // フィールドの順序変更
  const moveFieldUp = (index) => {
    if (index <= 0) return;
    setSelectedFields(prev => {
      const newFields = [...prev];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      return newFields;
    });
  };

  const moveFieldDown = (index) => {
    setSelectedFields(prev => {
      if (index >= prev.length - 1) return prev;
      const newFields = [...prev];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      return newFields;
    });
  };


  // QRスキャン処理
const handleScanFound = async (decoded) => {
  const code = String(decoded || '').trim();
  if (!code) return;

  const now = Date.now();
  if (lastScanRef.current.code === code && now - lastScanRef.current.ts < 2000) return;
  if (processingRef.current) return;

  processingRef.current = true;
  lastScanRef.current = { code, ts: now };
  // ← pause/resume 処理を削除

  try {
    const p = participants.find(p => p.qr_code === code);
    if (!p) {
      alert('該当する参加者が見つかりません');
      processingRef.current = false;  // ← 追加
      return;
    }

    const kind = !p.check_in_time ? 'checkin' : 'checkout';
    const label = kind === 'checkin' ? 'チェックイン' : 'チェックアウト';
    
    const success = await postCheck(p, kind); // ← 戻り値を受け取る
    
    if (success) { // ← 成功時のみアラートとモーダルを閉じる
      alert(`${p.participant_name} さんを${label}しました`);
      setReceptionOpen(false);
    }
  } finally {
    processingRef.current = false;
  }
};

  // const handleScanSubmit = async () => {
  //   const normalized = scanCode.normalize('NFKC');
  //   await handleScanFound(normalized);
  //   setScanCode('');
  // };

  // Excel出力
  const handleExportXlsx = () => {
    const headers = [
      ...(SHOW_ID_COL ? ['ID'] : []),
      '種別',
      ...(showAttendanceCol ? ['出欠'] : []),   // ← 追加
      '名前', 'メール', '電話番号', '備考',
      ...groupColumns.map(g => g.group_name),
      'チェックイン', 'チェックアウト',
    ];

    const source = filteredParticipants.length ? filteredParticipants : participants;
    const rows = source.map(p => [
      ...(SHOW_ID_COL ? [p.participant_id] : []),
      p.part_type_name ?? '',
      ...(showAttendanceCol ? [p.attendance_status ?? '未回答'] : []),
      p.participant_name ?? '',
      p.participant_email ?? '',
      p.participant_phone ?? '',
      p.participant_note ?? '',
      ...groupColumns.map(g => getGroupText(p, g.event_group_id)),
      p.check_in_time ? new Date(p.check_in_time).toLocaleString() : '',
      p.check_out_time ? new Date(p.check_out_time).toLocaleString() : '',
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map(h => ({ wch: h.length <= 4 ? 10 : h.length <= 8 ? 16 : 24 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'participants');
    const filename = `participants_${event?.event_title || ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename, { compression: true });
  };

    // 追加用 Excel テンプレートのダウンロード
    // handleDownloadTemplateXlsx を以下に置き換え
const handleDownloadTemplateXlsx = async () => {
  if (!event) {
    alert('イベント情報がまだ読み込まれていません');
    return;
  }

  const groups = event.event_groups || [];
  const workbook = new ExcelJS.Workbook();
  const mainSheet = workbook.addWorksheet('参加者一括登録');

  // ヘッダー設定
  const baseHeaders = ['お名前 *', 'メールアドレス', '電話番号（任意）', '備考（任意）'];
  const groupHeaders = groups.map(g => 
    g.required_category === 1 ? g.group_name + ' *' : g.group_name
  );
  const headers = [...baseHeaders, ...groupHeaders];
  
  mainSheet.addRow(headers);

  // 列幅設定
  mainSheet.columns = headers.map(h => ({
    width: h.length <= 4 ? 12 : h.length <= 10 ? 18 : 24
  }));

  // データ入力規則（プルダウン）を直接設定
  groups.forEach((g, gi) => {
    const mainColIndex = baseHeaders.length + gi + 1;

    // ★ datetime の場合：2行目にサンプルを1件だけ表示
    const isDatetime = (g.description || '').startsWith('日時');
    if (isDatetime) {
      const mode = (g.description || '').includes(':') ? g.description.split(':')[1] : 'ampm';
      let sample = '';
      if (mode === 'ampm') {
        const firstSchedule = event?.schedules?.[0];
        if (firstSchedule?.date) {
          sample = `${formatmdDate(new Date(firstSchedule.date))} 午前`;
        }
      } else {
        const slots = generateDatetimeSlots(event, mode);
        if (slots.length > 0) {
          sample = `${formatmdDate(slots[0])} ${formatTime(slots[0])}`;
        }
      }
      if (sample) {
        mainSheet.getCell(2, mainColIndex).value = sample;
      }
      return;
    }

    const types = (g.types || []).filter(t => t.event_type_name !== '');
    const isFreeText = g.types && g.types.length === 1 && g.types[0].event_type_name === '';
    if (isFreeText || types.length === 0) return;

    // 選択肢をカンマ区切りの文字列に
    const optionsList = types.map(t => t.event_type_name).join(',');

    // 2行目から200行目までプルダウンを設定
    for (let row = 2; row <= 200; row++) {
      mainSheet.getCell(row, mainColIndex).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${optionsList}"`],
        showErrorMessage: true,
        errorTitle: '入力エラー',
        error: 'リストから選択してください',
      };
    }
  });

  // ダウンロード
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  saveAs(blob, `参加者一括登録_${event.event_title || ''}.xlsx`);
};


  // Excel を読み込んで一括登録
  const handleImportXlsx = async (e) => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;
  if (!event) {
    alert('イベント情報がまだ読み込まれていません');
    e.target.value = '';
    return;
  }

  const normalizeForMatch = (v) =>
    String(v ?? '').trim().normalize('NFKC');

  try {
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

    if (!rows.length) {
      alert('Excel にデータがありません');
      return;
    }

    // ① ヘッダ行を取得 & 正規化
    const rawHeader = rows[0];
    const header = rawHeader.map((h) => String(h ?? '').trim());
    // 末尾の " *" を除去した配列（検索用）
    const normalizedHeader = header.map((h) =>
      h.replace(/\s*\*$/, '')
    );

    // ラベルから列インデックスを取得する共通関数
    const findColIndex = (label) => {
      const base = String(label ?? '').trim().replace(/\s*\*$/, '');
      return normalizedHeader.indexOf(base);
    };

    const body = rows.slice(1);

    // ② 各列インデックスを normalizedHeader から取得
    const idxName  = findColIndex('お名前');
    const idxMail  = findColIndex('メールアドレス');
    const idxPhone = findColIndex('電話番号（任意）');
    const idxnote  = findColIndex('備考（任意）');

    if (idxName === -1) {
      alert('「お名前」列が見つかりません。テンプレートから作成したファイルを使用してください。');
      return;
    }

    const groups = event.event_groups || [];
    const participantsPayload = [];
    const errors = [];

    body.forEach((row, rowIndex) => {
      const rawName = row[idxName];
      const name = rawName ? String(rawName).trim() : '';

      let rowError = false;

      if (!name) {
        errors.push({
          row: rowIndex + 2,
          message: '「お名前」が入力されていません。',
        });
        rowError = true;
      }

      const email = idxMail  >= 0 ? String(row[idxMail]  || '').trim() : '';
      const phone = idxPhone >= 0 ? String(row[idxPhone] || '').trim() : '';
      const note  = idxnote  >= 0 ? String(row[idxnote]  || '').trim() : '';
      const group_answers = [];

      groups.forEach((g) => {
        // ③ グループ列も、" *" なしの名前で検索
        const colIndex = findColIndex(g.group_name);
        if (colIndex === -1) return;

        const cellValue = row[colIndex];
        const valueStr = cellValue == null ? '' : String(cellValue).trim();
        const types = g.types || [];
        const isFreeText = types.length === 1 && types[0].event_type_name == '';

        // ④ 必須チェックはここで効くようになる
        if (g.required_category === 1 && !valueStr) {
          errors.push({
            row: rowIndex + 2,
            message: `必須項目「${g.group_name}」が入力されていません。`,
          });
          rowError = true;
          return;
        }

        if (!valueStr) return;

        if (!isFreeText) {
          const candidates = types.filter((t) => t.event_type_name != '');
          const matched = candidates.find((t) =>
            normalizeForMatch(t.event_type_name) === normalizeForMatch(valueStr)
          );

          if (!matched) {
            errors.push({
              row: rowIndex + 2,
              message: `グループ「${g.group_name}」に不正な値「${valueStr}」が入力されています。`,
            });
            rowError = true;
            return;
          }

          group_answers.push({
            event_group_id: g.event_group_id,
            event_type_id: matched.event_type_id,
            free_text: null,
          });
        } else {
          // ↓ 自由入力の場合、display_id に応じた文字数チェック
          const maxLen =
            (g.display_id === 2 || g.display_id === 3) ? 13 :
            (g.display_id === 4 || g.display_id === 5) ? 23 :
            null;

          if (maxLen && valueStr.length > maxLen) {
            errors.push({
              row: rowIndex + 2,
              message: `グループ「${g.group_name}」は${maxLen}文字以内で入力してください。（入力値: ${valueStr.length}文字）`,
            });
            rowError = true;
            return;
          }

          group_answers.push({
            event_group_id: g.event_group_id,
            event_type_id: null,
            free_text: valueStr,
          });
        }
      });

      if (!rowError) {
        participantsPayload.push({
          participant_name: name,
          participant_email: email,
          participant_phone: phone,
          participant_note: note,
          group_answers,
        });
      }
    });

    if (errors.length > 0) {
      let msg = '以下のエラーがあります。Excel を修正して再アップロードしてください。\n\n';

      errors.forEach((err) => {
        msg += `行 ${err.row}: ${err.message}\n`;
      });

      alert(msg);
      return;
    }

    if (!participantsPayload.length) {
      alert('追加対象となる行がありませんでした');
      return;
    }

    const res = await axios.post(
      `${API_URL}/api/operator/events/${invite_token}/participants/bulk/`,
      { participants: participantsPayload }
    );

    await fetchDetail();
    alert(`${res.data.created_count} 件の参加者を登録しました`);
  } catch (err) {
    console.error(err);
    alert('Excelの読み込みまたは一括登録に失敗しました');
  } finally {
    e.target.value = '';
  }
};

  // useMemo
  const groupColumns = useMemo(() => event?.event_groups || [], [event]);

  const filteredParticipants = useMemo(() => {
    const terms = normalizeText(searchQuery).split(/\s+/).filter(Boolean);

    return participants.filter(p => {
      // 出欠フィルタ
      if (attendanceFilter) {
        const status = p.attendance_status || '未回答';
        if (status !== attendanceFilter) return false;
      }

      // キーワードフィルタ
      if (terms.length) {
        const text = normalizeText([
          p.participant_name,
          p.participant_email,
          p.participant_phone,
          p.participant_note,
          p.part_type_name,
          ...groupColumns.map(g => getGroupText(p, g.event_group_id)),
        ].join(' '));
        if (!terms.every(t => text.includes(t))) return false;
      }

      return true;
    });
  }, [participants, searchQuery, attendanceFilter, groupColumns]);

  const isTodayInRange = useMemo(() => {
    if (!event) return false;
    const schedules = event.schedules || [];
    if (schedules.length === 0) return false;
    const today = toYmdNumber(new Date());
    return schedules.some(s => {
      const d = new Date(s.date);
      return toYmdNumber(d) === today;
    });
  }, [event]);

  const canSubmitAdd = useMemo(() => {
    if (!newName.trim() || saving) return false;

    const groups = event?.event_groups || [];

    const invalidRequiredGroups = groups.filter(g => {
      if (g.required_category !== 1) return false;
      const types = g.types || [];
      const isDatetime = (g.description || '').startsWith('日時');
      const isFreeText = !isDatetime && types.length === 1 && types[0].event_type_name === '';

      if (isDatetime || isFreeText) {
        const v = (groupTexts[g.event_group_id] ?? '').trim();
        return v.length === 0;
      } else {
        const v = groupSelections[g.event_group_id] ?? '';
        return !v;
      }
    });

    return invalidRequiredGroups.length === 0;
  }, [newName, saving, event, groupSelections, groupTexts]);


  const canSubmitEdit = useMemo(() => {
    if (!editName.trim() || editSaving) return false;

    const groups = event?.event_groups || [];

    const invalidRequiredGroups = groups.filter(g => {
      if (g.required_category !== 1) return false;
      const types = g.types || [];
      const isDatetime = (g.description || '').startsWith('日時');
      const isFreeText = !isDatetime && types.length === 1 && types[0].event_type_name === '';
      if (isDatetime || isFreeText) {
        const v = (editGroupTexts[g.event_group_id] ?? '').trim();  // ✅ editGroupTextsに修正
        return v.length === 0;                                       // ✅ returnを追加
      } else {
        const v = editGroupSelections[g.event_group_id] ?? '';       // ✅ editGroupSelectionsに修正
        return !v;
      }
    });

    return invalidRequiredGroups.length === 0;
  }, [editName, editSaving, event, editGroupSelections, editGroupTexts]);

  /// 招待状が表示できるかどうか（WEB表示の場合は一括PDF保存を表示しない）
  const canShowInvite = useMemo(() => {
    const checkinType = (event?.checkin_type ?? '').trim();
    if (!checkinType) return false;
    return checkinType.toUpperCase() !== 'WEB表示';
  }, [event]);


  const showAttendanceCol = useMemo(
    () =>
      event?.visibility === '全体公開(出欠表示)' ||
      event?.visibility === '招待制',
    [event]
  );


  // ファイル名プレビュー
  const filenamePreview = useMemo(() => {
    if (filteredParticipants.length === 0) return '（プレビューなし）';
    return generateFilename(filteredParticipants[0]);
  }, [filteredParticipants, generateFilename]);

  // 参加予定数 / 参加数（全体）
  const overallCounts = useMemo(() => {
    const parts = metricsData.participants || [];
    const actual  = parts.filter(p => p.status === '参加' && p.checked_in).length;
    return { actual };
  }, [metricsData]);

  // 項目別カウント（組み合わせユニーク数）
  const selectionCounts = useMemo(() => {
    if (selectionFilter.length === 0) return { planned: null, actual: null };
    const parts = metricsData.participants || [];
    const actualKeys  = new Set();
    parts.forEach(p => {
      if (p.status !== '参加') return;
      const key = selectionFilter
        .map(gid => {
          const v = p.answers[gid];
          return v == null ? '__NULL__' : v;
        })
        .join('||');
      if (p.checked_in) actualKeys.add(key);
    });
    return { actual: actualKeys.size };
  }, [metricsData, selectionFilter]);

  // useEffect
  useEffect(() => {
    fetchDetail();
  }, [invite_token]);

  useEffect(() => {
  if (receptionOpen) {
    const timer = setTimeout(() => {
      scanInputRef.current?.focus({ preventScroll: true });
    }, 500);
    return () => clearTimeout(timer);
  }
}, [receptionOpen]);

  useEffect(() => {
    // if (!receptionOpen || !isTodayInRange) return;
   if (!receptionOpen) return;

  // 状態をリセット ← 追加
  setCameraAvailable(true);
  setCameraError('');

  // 入力フィールドにフォーカス ← 追加
  setTimeout(() => {
    scanInputRef.current?.focus();
  }, 300);

  // カメラが利用可能かチェック ← 追加（この関数全体が新規）
  const checkCameraAndStart = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(d => d.kind === 'videoinput');
    
    if (videoDevices.length === 0) {
      setCameraAvailable(false);
      setCameraError('カメラが検出されませんでした。コード入力で受付してください。');
      return;
    }

    const html5Qrcode = new Html5Qrcode('qr-reader');
    qrScannerRef.current = html5Qrcode;

    await html5Qrcode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (decodedText) => {
        handleScanFound(decodedText);
      },
      () => {}
    );
  } catch (err) {
    console.error('カメラ起動エラー:', err);
    setCameraAvailable(false);
    
    // ★ここを追加：start()に失敗したらrefをクリア
    qrScannerRef.current = null;
    
    // エラーメッセージ設定（既存のコード）
    if (err.name === 'NotAllowedError') {
      setCameraError('カメラの使用が許可されていません。コード入力で受付してください。');
    } else if (err.name === 'NotFoundError') {
      setCameraError('カメラが見つかりませんでした。コード入力で受付してください。');
    } else if (err.name === 'NotReadableError') {
      setCameraError('カメラにアクセスできません。他のアプリで使用中の可能性があります。');
    } else {
      setCameraError('カメラを起動できませんでした。コード入力で受付してください。');
    }
  }
};

  // navigator.mediaDevices が利用可能かチェック ← 追加
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    setCameraAvailable(false);
    setCameraError('このブラウザはカメラに対応していません。コード入力で受付してください。');
  } else {
    checkCameraAndStart();
  }

  // 翻訳処理は既存のまま
  const root = document.getElementById('qr-reader');
  const translations = { /* ... 既存のまま ... */ };
  const updateLabels = () => { /* ... 既存のまま ... */ };
  updateLabels();
  const observer = new MutationObserver(updateLabels);
  if (root) {
    observer.observe(root, { childList: true, characterData: true, subtree: true });
  }

  return () => {
    observer.disconnect();
    if (qrScannerRef.current) {  // ← 修正
      qrScannerRef.current.stop().catch(() => {});
      qrScannerRef.current = null;
    }
  };
}, [receptionOpen]);

  // レンダリング関数
  const renderError = () => (
    error && <div className="hev-error-alert">{error}</div>
  );

  const renderMetricsCards = () => {
    const groups = metricsData.groups || [];
    const items = groups.map(g => ({
      key: String(g.event_group_id),
      label: g.group_name,
    }));

    const toggleItem = (key) => {
      setSelectionFilter(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      );
    };

    const allKeys = items.map(it => it.key);
    const allChecked = allKeys.length > 0 && allKeys.every(k => selectionFilter.includes(k));
    const toggleAll = () => setSelectionFilter(allChecked ? [] : allKeys);

    const fmt = (v) => (v == null ? '-' : v);

    return (
      <div className="hev-metricsRow">
       
        <div className="hev-metricCard hev-metricCard--selection">
          <div className="hev-metricCard__label">参加状況</div>

          <div className="hev-selectionDropdown" ref={selectionDropdownRef}>
            <button
              type="button"
              className="hev-selectionDropdown__toggle"
              onClick={() => setSelectionDropdownOpen(o => !o)}
            >
              <span>
                {selectionFilter.length === 0
                  ? '項目を選択'
                  : `${selectionFilter.length}項目 選択中`}
              </span>
              <span>{selectionDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {selectionDropdownOpen && (
              <div className="hev-selectionDropdown__menu">
                {items.length === 0 ? (
                  <div className="hev-selectionDropdown__empty">項目がありません</div>
                ) : (
                  <>
                    <label className="hev-selectionDropdown__item hev-selectionDropdown__item--all">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                      <span>すべて選択</span>
                    </label>

                    {items.map(it => (
                      <label key={it.key} className="hev-selectionDropdown__item">
                        <input
                          type="checkbox"
                          checked={selectionFilter.includes(it.key)}
                          onChange={() => toggleItem(it.key)}
                        />
                        <span>{it.label}</span>
                      </label>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="hev-metricCard__nums">
             <div className="hev-metricCard__num">
              <div className="hev-metricCard__numLabel">参加数</div>
              <div className="hev-metricCard__numValue">{overallCounts.actual}</div>
            </div>
            <div className="hev-metricCard__num">
              <div className="hev-metricCard__numLabel">項目別参加数</div>
              <div className="hev-metricCard__numValue">{fmt(selectionCounts.actual)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEventHeader = () => (
    <div className="hev-eventInfo hev-eventInfo--header">
      <div className="hev-eventInfo__main">
        <div className="hev-eventInfo__title">
          <span className="hev-eventInfo__eyebrow">イベント詳細</span>
          <h1 className="hev-eventInfo__h1">{event.event_title}</h1>
        </div>

        <div className="hev-eventInfo__meta">
          <div>
            {event.schedules && event.schedules.length > 1 ? (
              event.times_unified ? (
                // 複数日・同時刻：日付範囲＋時刻
                <div>
                  <b>期間：</b>
                  {`${formatDate(new Date(event.schedules[0].date))}～${formatDate(new Date(event.schedules[event.schedules.length - 1].date))}　${event.schedules[0].start_time?.slice(0, 5)}～${event.schedules[0].end_time?.slice(0, 5)}`}
                </div>
              ) : (
                // 複数日・異なる時刻：日程ごとに表示
                <>
                  {event.schedules.map((s, i) => (
                    <div key={i} style={{ display: 'flex' }}>
                      <span
                        style={{
                          minWidth: '3.5em',
                          fontWeight: i === 0 ? 'bold' : 'normal',
                          color: i === 0 ? 'inherit' : 'transparent',
                        }}
                      >
                        期間
                      </span>
                      <span>
                        {i === 0 ? '：' : '　'}
                        {formatDate(new Date(s.date))}　{s.start_time?.slice(0, 5)}～{s.end_time?.slice(0, 5)}
                        {s.recept_start_time && s.recept_end_time
                          ? `　受付（${s.recept_start_time.slice(0, 5)}～${s.recept_end_time.slice(0, 5)}）`
                          : ''}
                      </span>
                    </div>
                  ))}
                </>
              )
            ) : (
              // 単日
              <div><b>期間：</b>{formatPeriod(event)}</div>
            )}
          </div>

          <div><b>場所:</b> {event.event_location || '-'}</div>

          {event.capacity !== 0 && <div><b>定員:</b> {event.capacity || '-'}名</div>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span><b>イベントタイプ:</b> {event.visibility || '-'}</span>
            {event.visibility === '招待制' && (
              <button
                className="hev-btn hev-btn--primary"
                onClick={openInviteModal}
              >
                ユーザー招待
              </button>
            )}
          </div>

          <div>
            <b>招待URL:</b>{' '}
            {inviteUrl ? (
              <a href={inviteUrl} target="_blank" rel="noreferrer">
                {inviteUrl}
              </a>
            ) : (
              '-'
            )}
          </div>
        </div>
      </div>

      <div className="hev-eventInfo__metrics">
        {renderMetricsCards()}
      </div>
    </div>
  );

  const renderSectionHeader = () => (
    <div className="hev-sectionHead">
        
      <input
        className="hev-input"
        style={{ maxWidth: 320 }}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="キーワードで絞り込み"
      />

      {showAttendanceCol && (
        <select
          className="hev-input"
          style={{ maxWidth: 140 }}
          value={attendanceFilter}
          onChange={(e) => setAttendanceFilter(e.target.value)}
        >
          <option value="">すべての出欠</option>
          <option value="参加">参加</option>
          <option value="不参加">不参加</option>
          <option value="キャンセル">キャンセル</option>
          <option value="未回答">未回答</option>
        </select>
      )}

       <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
          （{filteredParticipants.length} / {participants.length}）
       </span>
      
      <button 
        className={`hev-btn${filteredParticipants.length === 0 ? ' is-disabled' : ''}`}
        disabled={filteredParticipants.length === 0}
        onClick={handleExportXlsx}
        style={{ marginLeft: 8 }}
        >
        Excel出力
      </button>
      {canShowInvite && (
      <button
        className={`hev-btn${filteredParticipants.length === 0 ? ' is-disabled' : ''}`}
        disabled={filteredParticipants.length === 0}
        onClick={() => setPdfModalOpen(true)}
        style={{ marginLeft: 8 }}
      >
        一括PDF保存
      </button>
    )}
      <div className="hev-spacer" />
      <button
        // className={`hev-btn hev-btn--primary${!isTodayInRange ? ' is-disabled' : ''}`}
        // disabled={!isTodayInRange}
        className={`hev-btn hev-btn--primary`}
        onClick={() => setReceptionOpen(true)}
        style={{ marginRight: 8 }}
      >
        受付
      </button>
      <button className="hev-btn hev-btn--primary" onClick={() => { initGroupInputs(event); setAddModalOpen(true); }}>
        参加者追加
      </button>
    </div>
  );

  const renderReceptionModal = () => (
    receptionOpen && (
      <div className="hev-modalOverlay" onClick={() => setReceptionOpen(false)}>
        <div className="hev-modalBody" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">受付</div>
            <div className="hev-spacer" />
            <button className="hev-btn" onClick={() => setReceptionOpen(false)}>閉じる</button>
          </div>
          <div className="hev-modalContent">
            {/* {isTodayInRange ? (
              <div id="qr-reader" style={{ width: '100%', maxWidth: 480, margin: '0 auto 16px' }} />
            ) : (
              <div style={{ color: '#6b7280', marginBottom: 12 }}>本日は受付できません</div>
            )} */}
            {cameraAvailable ? (
            <div id="qr-reader" style={{ width: '100%', maxWidth: 480, margin: '0 auto 16px' }} />
          ) : (
            <div style={{ 
              textAlign: 'center', 
              color: '#6b7280', 
              marginBottom: 16, 
              padding: '20px',
              backgroundColor: '#f3f4f6',
              borderRadius: 8
            }}>
              {cameraError}
            </div>
          )}

           <label className="hev-field" style={{ marginTop: 8 }}>
              <span className="hev-fieldLabel">QR / コードを入力</span>
              <input
                ref={scanInputRef}
                className="hev-input"
                type="text"
                readOnly          // ← IMEに渡さない
                placeholder="QRコードをスキャン or コードを入力"
                value={scanCode}
              />
            </label>
            <div className="hev-modalFoot" style={{ justifyContent: 'flex-start' }}>
              {/* <button
                // className={`hev-btn hev-btn--primary${!isTodayInRange ? ' is-disabled' : ''}`}
                // disabled={!isTodayInRange}
                className={`hev-btn hev-btn--primary`}
                onClick={handleScanSubmit}
              >
                コードで受付
              </button> */}
            </div>
          </div>
        </div>
      </div>
    )
  );

  const renderGroupField = (g) => {
    const gid = g.event_group_id;
    const types = g.types || [];
    const isFreeText = types.length === 1 && types[0].event_type_name === '';
    const label = g.description ? `${g.group_name}（${g.description}）` : g.group_name;
    const required = g.required_category === 1;

     const isDatetime = (g.description || '').startsWith('日時');
      if (isDatetime) {
        const mode = (g.description || '').includes(':') ? g.description.split(':')[1] : 'ampm';
        let options = [];
        if (mode === 'ampm') {
          const schedules = event?.schedules || [];

          options = schedules.flatMap(s => {
            const date = new Date(s.date);
            const dateStr = formatmdDate(date); // 04/22(水) みたいな

            return [
              `${dateStr} 午前`,
              `${dateStr} 午後`
            ];
          });
        } else {
          options = generateDatetimeSlots(event, mode).map(slot => `${formatmdDate(slot)} ${formatTime(slot)}`);
        }
        const datalistId = `datalist-add-${gid}`;
        return (
          <label className="hev-field" key={gid}>
            <span className="hev-fieldLabel">{g.group_name}{required ? ' *' : ''}</span>
            <DatetimeComboBox
              value={groupTexts[gid] ?? ''}
              onChange={(val) => setGroupTexts(s => ({ ...s, [gid]: val }))}
              options={options}
            />
          </label>
        );
      }


    if (isFreeText) {
      const maxLen =
        (g.display_id === 2 || g.display_id === 3) ? 13 :
        (g.display_id === 4 || g.display_id === 5) ? 23 :
        undefined;
      return (
        <label className="hev-field" key={gid}>
          <span className="hev-fieldLabel">
            {label}{required ? ' *' : ''}
            {maxLen && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 8, fontWeight: 'normal' }}>
                {(groupTexts[gid] ?? '').length} / {maxLen}
              </span>
            )}
          </span>
          <input
            className="hev-input"
            type="text"
            value={groupTexts[gid] ?? ''}
            onChange={(e) => setGroupTexts(s => ({ ...s, [gid]: e.target.value }))}
            placeholder={maxLen ? `入力してください（${maxLen}文字以内）` : '入力してください'}
            maxLength={maxLen}
          />
        </label>
      );
    }

    return (
      <label className="hev-field" key={gid}>
        <span className="hev-fieldLabel">{label}{required ? ' *' : ''}</span>
        <select
          className="hev-input"
          value={groupSelections[gid] ?? ''}
          onChange={(e) => setGroupSelections(s => ({ ...s, [gid]: e.target.value }))}
        >
          <option value="">選択してください</option>
          {types.map(t => (
            <option key={t.event_type_id} value={t.event_type_id}>
              {t.event_type_name ?? '（名称未設定）'}
              {t.event_type_price > 0 ? `（${t.event_type_price.toLocaleString()}円）` : ''}
            </option>
          ))}
        </select>
      </label>
    );
  };

  const renderAddModal = () => (
    addModalOpen && (
      <div className="hev-modalOverlay">
        <div className="hev-modalBody" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">参加者を追加</div>
            <div className="hev-spacer" />
            <button className="hev-btn" onClick={() => setAddModalOpen(false)}>閉じる</button>
          </div>

          <div className="hev-modalContent">
            <div className="hev-addModalSections">
              <div className="hev-addSection">
                <div className="hev-formGrid">
                  <label className="hev-field">
                    <span className="hev-fieldLabel">お名前 *</span>
                    <input
                      className="hev-input"
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      autoFocus
                      placeholder="山田 太郎"
                    />
                  </label>

                  <label className="hev-field">
                    <span className="hev-fieldLabel">メールアドレス</span>
                    <input
                      className="hev-input"
                      type="email"
                      value={newMail}
                      onChange={(e) => setNewMail(e.target.value)}
                      placeholder="taro@example.com"
                    />
                  </label>

                  <label className="hev-field">
                    <span className="hev-fieldLabel">電話番号（任意）</span>
                    <input
                      className="hev-input"
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="090-1234-5678"
                    />
                  </label>

                  <label className="hev-field">
                    <span className="hev-fieldLabel">備考（任意）</span>
                    <input
                      className="hev-input"
                      type="note"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                    />
                  </label>

                  {event?.event_groups?.map(renderGroupField)}
                </div>

                <div className="hev-modalFoot hev-addModalFoot">
                  <button className="hev-btn" onClick={() => setAddModalOpen(false)}>キャンセル</button>
                  <button
                    className={`hev-btn hev-btn--primary${!canSubmitAdd ? ' is-disabled' : ''}`}
                    disabled={!canSubmitAdd}
                    onClick={addParticipant}
                  >
                    {saving ? '追加中…' : '追加する'}
                  </button>
                </div>
              </div>

              <hr className="hev-divider" />

              <div className="hev-addSection">
                <div className="hev-addSectionTitle">Excelでまとめて追加</div>

                <p className="hev-addNote">
                  「テンプレートをダウンロード」でExcelを取得し、参加者情報を入力してからアップロードしてください。
                </p>

                <p className="hev-addNote">
                  選択項目はシートのプルダウンの内容を選択してください。
                </p>

                <div className="hev-modalFoot hev-addModalFootLeft">
                  <button className="hev-btn" onClick={handleDownloadTemplateXlsx}>
                    テンプレートをダウンロード
                  </button>

                  <label className="hev-btn hev-btn--primary hev-fileButton">
                    Excelをアップロード
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleImportXlsx}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );

  const renderEditGroupField = (g) => {
    const gid = g.event_group_id;
    const types = g.types || [];
    const isFreeText = types.length === 1 && types[0].event_type_name === '';
    const label = g.description ? `${g.group_name}（${g.description}）` : g.group_name;
    const required = g.required_category === 1;

    const isDatetime = (g.description || '').startsWith('日時');
    if (isDatetime) {
      const mode = (g.description || '').includes(':') ? g.description.split(':')[1] : 'ampm';
      let options = [];
      if (mode === 'ampm') {
        const schedules = event?.schedules || [];

        options = schedules.flatMap(s => {
          const date = new Date(s.date);
          const dateStr = formatmdDate(date); // 04/22(水) みたいな

          return [
            `${dateStr} 午前`,
            `${dateStr} 午後`
          ];
        });
      } else {
        options = generateDatetimeSlots(event, mode).map(slot => `${formatmdDate(slot)} ${formatTime(slot)}`);
      }
      const datalistId = `datalist-edit-${gid}`;
      return (
        <label className="hev-field" key={gid}>
          <span className="hev-fieldLabel">{g.group_name}{required ? ' *' : ''}</span>
          <DatetimeComboBox
            value={editGroupTexts[gid] ?? ''}
            onChange={(val) => setEditGroupTexts(s => ({ ...s, [gid]: val }))}
            options={options}
          />
        </label>
      );
    }

    if (isFreeText) {
      const maxLen =
        (g.display_id === 2 || g.display_id === 3) ? 13 :
        (g.display_id === 4 || g.display_id === 5) ? 23 :
        undefined;
      return (
        <label className="hev-field" key={gid}>
          <span className="hev-fieldLabel">
            {label}{required ? ' *' : ''}
            {maxLen && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 8, fontWeight: 'normal' }}>
                {(editGroupTexts[gid] ?? '').length} / {maxLen}
              </span>
            )}
          </span>
          <input
            className="hev-input"
            type="text"
            value={editGroupTexts[gid] ?? ''}
            onChange={(e) => setEditGroupTexts(s => ({ ...s, [gid]: e.target.value }))}
            placeholder={maxLen ? `入力してください（${maxLen}文字以内）` : '入力してください'}
            maxLength={maxLen}
          />
        </label>
      );
    }

    return (
      <label className="hev-field" key={gid}>
        <span className="hev-fieldLabel">{label}{required ? ' *' : ''}</span>
        <select
          className="hev-input"
          value={editGroupSelections[gid] ?? ''}
          onChange={(e) => setEditGroupSelections(s => ({ ...s, [gid]: e.target.value }))}
        >
          <option value="">選択してください</option>
          {types.map(t => (
            <option key={t.event_type_id} value={t.event_type_id}>
              {t.event_type_name ?? '（名称未設定）'}
              {t.event_type_price > 0 ? `（${t.event_type_price.toLocaleString()}円）` : ''}
            </option>
          ))}
        </select>
      </label>
    );
  };

  const renderEditModal = () => (
    editModalOpen && editingParticipant && (
      <div className="hev-modalOverlay">
        <div className="hev-modalBody" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">参加者を編集</div>
            <div className="hev-spacer" />
            <button
              className="hev-btn"
              onClick={() => { setEditModalOpen(false); setEditingParticipant(null); }}
            >
              閉じる
            </button>
          </div>

          <div className="hev-modalContent">
            <div className="hev-formGrid">
              <label className="hev-field">
                <span className="hev-fieldLabel">お名前 *</span>
                <input
                  className="hev-input"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                />
              </label>

              <label className="hev-field">
                <span className="hev-fieldLabel">メールアドレス</span>
                <input
                  className="hev-input"
                  type="email"
                  value={editMail}
                  onChange={(e) => setEditMail(e.target.value)}
                />
              </label>

              <label className="hev-field">
                <span className="hev-fieldLabel">電話番号（任意）</span>
                <input
                  className="hev-input"
                  type="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                />
              </label>

              <label className="hev-field">
                <span className="hev-fieldLabel">備考（任意）</span>
                <input
                  className="hev-input"
                  type="text"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </label>

              <label className="hev-field">
                <span className="hev-fieldLabel">チェックイン</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="hev-input"
                    type="datetime-local"
                    value={editCheckIn}
                    onChange={(e) => setEditCheckIn(e.target.value)}
                  />
                  {editCheckIn && (
                    <button
                      className="hev-btn hev-btn--ghost"
                      type="button"
                      onClick={() => setEditCheckIn('')}
                    >
                      クリア
                    </button>
                  )}
                </div>
              </label>

              <label className="hev-field">
                <span className="hev-fieldLabel">チェックアウト</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="hev-input"
                    type="datetime-local"
                    value={editCheckOut}
                    onChange={(e) => setEditCheckOut(e.target.value)}
                  />
                  {editCheckOut && (
                    <button
                      className="hev-btn hev-btn--ghost"
                      type="button"
                      onClick={() => setEditCheckOut('')}
                    >
                      クリア
                    </button>
                  )}
                </div>
              </label>

              {showAttendanceCol && (
                <label className="hev-field">
                  <span className="hev-fieldLabel">出欠</span>
                  <select
                    className="hev-input"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="参加">参加</option>
                    <option value="不参加">不参加</option>
                    <option value="キャンセル">キャンセル</option>
                  </select>
                </label>
              )}

              {event?.event_groups?.map(renderEditGroupField)}
            </div>

            <div className="hev-modalFoot hev-addModalFoot">
              <button
                className="hev-btn"
                onClick={() => { setEditModalOpen(false); setEditingParticipant(null); }}
              >
                キャンセル
              </button>
              <button
                className={`hev-btn hev-btn--primary${!canSubmitEdit ? ' is-disabled' : ''}`}
                disabled={!canSubmitEdit}
                onClick={updateParticipant}
              >
                {editSaving ? '更新中…' : '更新する'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  
  // 一括PDF保存モーダル
  const renderPdfModal = () => (
    pdfModalOpen && (
      <div className="hev-modalOverlay">
        <div className="hev-modalBody hev-modalBody--wide" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">一括PDF保存</div>
            <div className="hev-spacer" />
            <button className="hev-btn" onClick={() => setPdfModalOpen(false)} disabled={pdfGenerating}>
              閉じる
            </button>
          </div>

          <div className="hev-modalContent">
            {pdfGenerating ? (
              <div className="hev-pdfProgress">
                <div className="hev-pdfProgress__text">
                  PDF生成中... ({pdfProgress.current} / {pdfProgress.total})
                </div>
                <div className="hev-pdfProgress__bar">
                  <div 
                    className="hev-pdfProgress__fill"
                    style={{ width: `${(pdfProgress.current / pdfProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="hev-pdfInfo">
                  <p>表示中の参加者 <strong>{filteredParticipants.length}名</strong> の招待状PDFをZIPファイルで一括ダウンロードします。</p>
                </div>

                <div className="hev-pdfSettings">
                  <h4>ファイル名の設定</h4>
                  
                  <div className="hev-pdfSettings__row">
                    <label className="hev-field">
                      <span className="hev-fieldLabel">接頭辞（任意）</span>
                      <input
                        className="hev-input"
                        type="text"
                        value={filenamePrefix}
                        onChange={(e) => setFilenamePrefix(e.target.value)}
                        placeholder="例: 招待状"
                      />
                    </label>

                    <label className="hev-field">
                      <span className="hev-fieldLabel">接尾辞（任意）</span>
                      <input
                        className="hev-input"
                        type="text"
                        value={filenameSuffix}
                        onChange={(e) => setFilenameSuffix(e.target.value)}
                        placeholder="例: 2024"
                      />
                    </label>
                  </div>

                  <div className="hev-pdfSettings__section">
                    <span className="hev-fieldLabel">区切り文字</span>
                    <div className="hev-pdfSettings__separators">
                      {SEPARATORS.map(s => (
                        <label key={s.id} className="hev-checkbox">
                          <input
                            type="radio"
                            name="separator"
                            checked={separator === s.id}
                            onChange={() => setSeparator(s.id)}
                          />
                          <span>{s.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="hev-pdfSettings__section">
                    <span className="hev-fieldLabel">ファイル名に含める項目（チェックした順番で使用）</span>
                    <div className="hev-pdfSettings__fields">
                      <label className="hev-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes('participant_name')}
                          onChange={() => toggleField('participant_name')}
                        />
                        <span>名前</span>
                      </label>
                      {groupColumns.map(g => (
                        <label key={g.event_group_id} className="hev-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(`group_${g.event_group_id}`)}
                            onChange={() => toggleField(`group_${g.event_group_id}`)}
                          />
                          <span>{g.group_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                 {selectedFields.length > 0 && (
                    <div className="hev-pdfSettings__section">
                      <span className="hev-fieldLabel">選択した項目の順序</span>
                      <div className="hev-pdfSettings__order">
                        {selectedFields.map((fieldId, index) => {
                          let label;
                          if (fieldId === 'participant_name') {
                            label = '名前';
                          } else if (fieldId.startsWith('group_')) {
                            const groupId = Number(fieldId.replace('group_', ''));
                            const group = groupColumns.find(g => g.event_group_id === groupId);
                            label = group?.group_name || fieldId;
                          } else {
                            label = fieldId;
                          }
                          return (
                            <div key={fieldId} className="hev-pdfSettings__orderItem">
                              <span>{index + 1}. {label}</span>
                              <div className="hev-pdfSettings__orderButtons">
                                <button
                                  className="hev-btn hev-btn--small"
                                  onClick={() => moveFieldUp(index)}
                                  disabled={index === 0}
                                >
                                  ↑
                                </button>
                                <button
                                  className="hev-btn hev-btn--small"
                                  onClick={() => moveFieldDown(index)}
                                  disabled={index === selectedFields.length - 1}
                                >
                                  ↓
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="hev-pdfSettings__preview">
                    <span className="hev-fieldLabel">ファイル名プレビュー</span>
                    <code>{filenamePreview}</code>
                  </div>
                </div>

                <div className="hev-modalFoot">
                  <button className="hev-btn" onClick={() => setPdfModalOpen(false)}>
                    キャンセル
                  </button>
                  <button
                    className="hev-btn hev-btn--primary"
                    onClick={generatePdfs}
                    disabled={filteredParticipants.length === 0}
                  >
                    PDF生成・ダウンロード
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  );

   const renderInviteModal = () => (
    inviteModalOpen && (
      <div className="hev-modalOverlay">
        <div className="hev-modalBody" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">ユーザーを招待</div>
            <div className="hev-spacer" />
            <button className="hev-btn" onClick={() => setInviteModalOpen(false)}>
              閉じる
            </button>
          </div>

          <div className="hev-modalContent">
            {/* 検索 */}
            <div style={{ marginBottom: 12 }}>
              <input
                className="hev-input"
                type="text"
                value={inviteSearch}
                onChange={(e) => setInviteSearch(e.target.value)}
                placeholder="名前・メールで絞り込み"
              />
            </div>

            {/* 選択件数 */}
            <div style={{ marginBottom: 8, color: '#6b7280', fontSize: '0.875rem' }}>
              {inviteSelectedIds.length}名 選択中 / 候補{filteredInviteCandidates.length}名
            </div>

            {/* ユーザー一覧 */}
            <div className="hev-inviteList">
              {inviteLoading ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                  読み込み中…
                </div>
              ) : filteredInviteCandidates.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                  候補ユーザーがいません
                </div>
              ) : (
                filteredInviteCandidates.map(u => (
                  <label
                    key={u.user_id}
                    className={`hev-inviteItem${u.is_registered ? ' is-disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      disabled={u.is_registered}
                      checked={inviteSelectedIds.includes(u.user_id)}
                      onChange={() => toggleInviteUser(u.user_id)}
                    />
                    <div className="hev-inviteItem__info">
                      <div className="hev-inviteItem__name">{u.user_name}</div>
                      <div className="hev-inviteItem__sub">
                        {u.user_email}
                        {u.phone ? ` / ${u.phone}` : ''}
                      </div>
                    </div>
                    {u.is_registered && (
                      <span className="hev-inviteItem__badge">登録済み</span>
                    )}
                  </label>
                ))
              )}
            </div>

            <div className="hev-modalFoot">
              <button className="hev-btn" onClick={() => setInviteModalOpen(false)}>
                キャンセル
              </button>
              <button
                className={`hev-btn hev-btn--primary${inviteSelectedIds.length === 0 ? ' is-disabled' : ''}`}
                disabled={inviteSelectedIds.length === 0 || inviting}
                onClick={submitInvite}
              >
                {inviting ? '招待中…' : `選択した ${inviteSelectedIds.length} 名を招待`}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );


  // PDF生成用の非表示レンダリング領域
  const renderPdfRenderArea = () => (
    currentRenderingParticipant && (
      <div 
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '1240px',
          height: '1754px',
          background: '#fff',
          zIndex: -1,
          overflow: 'hidden',
        }}
      >
        <div className="ip-printA4" style={{ width: '100%', height: '100%' }}>
          <InviteSheet
            event={currentRenderingParticipant.event}
            participant={currentRenderingParticipant.participant}
            qrCode={currentRenderingParticipant.qrCode}
            sheetRef={pdfSheetRef}
          />
        </div>
      </div>
    )
  );

  const renderParticipantsTable = () => (
    <div className="hev-tableWrap">
      <table className="hev-table">
        <thead>
          <tr>
            {SHOW_ID_COL && <th>ID</th>}
            <th>種別</th>
            {showAttendanceCol && <th>出欠</th>}
            <th>名前</th>
            <th>メール</th>
            <th>電話番号</th>
            <th>備考</th>
            {groupColumns.map(g => <th key={g.event_group_id}>{g.group_name}</th>)}
            <th>チェックイン</th>
            <th>チェックアウト</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredParticipants.length === 0 ? (
            <tr>
              <td colSpan={(SHOW_ID_COL ? 6 : 5) + groupColumns.length + 3} className="hev-empty">
                該当する参加者がいません
              </td>
            </tr>
          ) : (
            filteredParticipants.map(p => (
              <ParticipantRow
                key={p.qr_code}
                participant={p}
                groupColumns={groupColumns}
                isTodayInRange={isTodayInRange}
                checkinType={event?.checkin_type}
                showAttendanceCol={showAttendanceCol}
                onInvite={showInvite}
                onQr={showQr}
                onCheckIn={(p) => postCheck(p, 'checkin')}
                onCheckOut={(p) => postCheck(p, 'checkout')}
                onEdit={openEditModal}
                onDelete={onDelete} 
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderEventContent = () => {
    if (!event) return null;
    return (
      <>
        {renderEventHeader()}
        {renderSectionHeader()}
        {renderReceptionModal()}
        {renderAddModal()}
        {renderEditModal()}
        {renderPdfModal()}
        {renderInviteModal()}
        {renderPdfRenderArea()}
        {renderParticipantsTable()}
      </>
    );
  };

  return (
    <div className="hev-hostEventDetail">
      {renderError()}
      {renderEventContent()}
    </div>
  );
}